import type { RoomType, ProjectTypeConfig } from '@/types/firestore';
import {
  DEFAULT_OUTDOOR_PROJECT_TYPES,
  DECK_MATERIAL_OPTIONS,
  RAILING_OPTIONS,
  LIGHTING_OPTIONS,
  PATIO_SURFACE_OPTIONS,
  BORDER_OPTIONS,
  PERGOLA_STRUCTURE_OPTIONS,
  CANOPY_OPTIONS,
  POOL_DECKING_OPTIONS,
  POOL_FENCING_OPTIONS,
  GAZEBO_STRUCTURE_OPTIONS,
  SCREENING_OPTIONS,
  OUTDOOR_COUNTERTOP_OPTIONS,
  APPLIANCE_OPTIONS,
  FIRE_TYPE_OPTIONS,
  SEATING_OPTIONS,
  FENCE_MATERIAL_OPTIONS,
  FENCE_HEIGHT_OPTIONS,
  GATE_OPTIONS,
  HARDSCAPE_OPTIONS,
  PLANTER_OPTIONS,
  PATHWAY_LIGHTING_OPTIONS,
  FLOORING_OPTIONS,
  WALL_OPTIONS,
  CABINET_OPTIONS,
  COUNTERTOP_OPTIONS,
} from './roomOptions';

/**
 * Template for pre-configured project types that can be loaded into an area
 */
export interface ProjectTypeTemplate {
  id: string;
  name: string;
  description: string;
  forRoomTypes: RoomType[];
  projectTypes: ProjectTypeConfig[];
}

// ============================================
// OUTDOOR TEMPLATES
// ============================================

export const OUTDOOR_STANDARD_TEMPLATE: ProjectTypeTemplate = {
  id: 'outdoor_standard',
  name: 'Standard Outdoor Package',
  description: 'Deck, Patio, and Pergola with common options',
  forRoomTypes: ['outdoor'],
  projectTypes: [
    {
      id: 'deck',
      label: 'Deck',
      description: 'Wood or composite decking',
      selectionCategories: [DECK_MATERIAL_OPTIONS, RAILING_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'patio',
      label: 'Patio',
      description: 'Concrete, pavers, or stone',
      selectionCategories: [PATIO_SURFACE_OPTIONS, BORDER_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'pergola',
      label: 'Pergola',
      description: 'Shade structure with posts and beams',
      selectionCategories: [PERGOLA_STRUCTURE_OPTIONS, CANOPY_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

export const OUTDOOR_FULL_TEMPLATE: ProjectTypeTemplate = {
  id: 'outdoor_full',
  name: 'Full Outdoor Suite',
  description: 'All 9 outdoor project types',
  forRoomTypes: ['outdoor'],
  projectTypes: DEFAULT_OUTDOOR_PROJECT_TYPES,
};

export const OUTDOOR_ENTERTAINING_TEMPLATE: ProjectTypeTemplate = {
  id: 'outdoor_entertaining',
  name: 'Outdoor Entertaining',
  description: 'Pool, outdoor kitchen, and fire pit',
  forRoomTypes: ['outdoor'],
  projectTypes: [
    {
      id: 'pool_area',
      label: 'Pool Area',
      description: 'Pool surrounds, decking, outdoor kitchen',
      selectionCategories: [POOL_DECKING_OPTIONS, POOL_FENCING_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'outdoor_kitchen',
      label: 'Outdoor Kitchen',
      description: 'Built-in grill, counters, sink',
      selectionCategories: [OUTDOOR_COUNTERTOP_OPTIONS, APPLIANCE_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'fire_pit',
      label: 'Fire Pit Area',
      description: 'Fire pit with seating surrounds',
      selectionCategories: [FIRE_TYPE_OPTIONS, SEATING_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

export const OUTDOOR_STRUCTURES_TEMPLATE: ProjectTypeTemplate = {
  id: 'outdoor_structures',
  name: 'Outdoor Structures',
  description: 'Pergola, gazebo, and fencing',
  forRoomTypes: ['outdoor'],
  projectTypes: [
    {
      id: 'pergola',
      label: 'Pergola',
      description: 'Shade structure with posts and beams',
      selectionCategories: [PERGOLA_STRUCTURE_OPTIONS, CANOPY_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'gazebo',
      label: 'Gazebo',
      description: 'Freestanding covered structure',
      selectionCategories: [GAZEBO_STRUCTURE_OPTIONS, SCREENING_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'fence',
      label: 'Fence/Privacy',
      description: 'Fencing, lattice, privacy walls',
      selectionCategories: [FENCE_MATERIAL_OPTIONS, FENCE_HEIGHT_OPTIONS, GATE_OPTIONS],
    },
  ],
};

export const OUTDOOR_LANDSCAPING_TEMPLATE: ProjectTypeTemplate = {
  id: 'outdoor_landscaping',
  name: 'Landscaping Focus',
  description: 'Hardscaping, planters, pathways',
  forRoomTypes: ['outdoor'],
  projectTypes: [
    {
      id: 'landscape',
      label: 'Landscape Refresh',
      description: 'Hardscaping, planters, pathways',
      selectionCategories: [HARDSCAPE_OPTIONS, PLANTER_OPTIONS, PATHWAY_LIGHTING_OPTIONS],
    },
    {
      id: 'fence',
      label: 'Fence/Privacy',
      description: 'Fencing, lattice, privacy walls',
      selectionCategories: [FENCE_MATERIAL_OPTIONS, FENCE_HEIGHT_OPTIONS, GATE_OPTIONS],
    },
    {
      id: 'patio',
      label: 'Patio',
      description: 'Concrete, pavers, or stone',
      selectionCategories: [PATIO_SURFACE_OPTIONS, BORDER_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

// ============================================
// GARAGE TEMPLATES
// ============================================

export const GARAGE_STANDARD_TEMPLATE: ProjectTypeTemplate = {
  id: 'garage_standard',
  name: 'Standard Garage Refresh',
  description: 'Flooring, storage, organization',
  forRoomTypes: ['garage'],
  projectTypes: [
    {
      id: 'standard',
      label: 'Standard Refresh',
      description: 'Flooring, storage, organization',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

export const GARAGE_CONVERSION_TEMPLATE: ProjectTypeTemplate = {
  id: 'garage_conversion',
  name: 'Garage Conversion Package',
  description: 'Man cave, home gym, workshop options',
  forRoomTypes: ['garage'],
  projectTypes: [
    {
      id: 'man_cave',
      label: 'Man Cave',
      description: 'Entertainment space',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'home_gym',
      label: 'Home Gym',
      description: 'Fitness space',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'workshop',
      label: 'Workshop/Studio',
      description: 'Work and hobby space',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

export const GARAGE_ADU_TEMPLATE: ProjectTypeTemplate = {
  id: 'garage_adu',
  name: 'ADU/Living Space',
  description: 'Full living conversions',
  forRoomTypes: ['garage'],
  projectTypes: [
    {
      id: 'adu',
      label: 'Additional Living Space',
      description: 'Full ADU conversion',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, CABINET_OPTIONS, COUNTERTOP_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'guest_suite',
      label: 'Guest Suite',
      description: 'Mother-in-law suite',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

export const GARAGE_FULL_TEMPLATE: ProjectTypeTemplate = {
  id: 'garage_full',
  name: 'All Garage Options',
  description: 'All conversion types',
  forRoomTypes: ['garage'],
  projectTypes: [
    {
      id: 'standard',
      label: 'Standard Refresh',
      description: 'Flooring, storage, organization',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'adu',
      label: 'Additional Living Space',
      description: 'Full ADU conversion',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, CABINET_OPTIONS, COUNTERTOP_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'man_cave',
      label: 'Man Cave',
      description: 'Entertainment space',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'guest_suite',
      label: 'Guest Suite',
      description: 'Mother-in-law suite',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'home_gym',
      label: 'Home Gym',
      description: 'Fitness space',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'workshop',
      label: 'Workshop/Studio',
      description: 'Work and hobby space',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

// ============================================
// BASEMENT TEMPLATES
// ============================================

export const BASEMENT_STANDARD_TEMPLATE: ProjectTypeTemplate = {
  id: 'basement_standard',
  name: 'Standard Basement Finish',
  description: 'Basic finishing with common options',
  forRoomTypes: ['basement'],
  projectTypes: [
    {
      id: 'full_finish',
      label: 'Full Finish',
      description: 'Complete basement finishing',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'rec_room',
      label: 'Recreation Room',
      description: 'Family room / entertainment',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

export const BASEMENT_ENTERTAINMENT_TEMPLATE: ProjectTypeTemplate = {
  id: 'basement_entertainment',
  name: 'Entertainment Basement',
  description: 'Home theater, bar, game room',
  forRoomTypes: ['basement'],
  projectTypes: [
    {
      id: 'home_theater',
      label: 'Home Theater',
      description: 'Dedicated movie/viewing room',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'wet_bar',
      label: 'Wet Bar',
      description: 'Bar area with sink',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, CABINET_OPTIONS, COUNTERTOP_OPTIONS, LIGHTING_OPTIONS],
    },
    {
      id: 'game_room',
      label: 'Game Room',
      description: 'Billiards, arcade, gaming',
      selectionCategories: [FLOORING_OPTIONS, WALL_OPTIONS, LIGHTING_OPTIONS],
    },
  ],
};

// ============================================
// ALL TEMPLATES COLLECTION
// ============================================

export const PROJECT_TYPE_TEMPLATES: ProjectTypeTemplate[] = [
  // Outdoor
  OUTDOOR_STANDARD_TEMPLATE,
  OUTDOOR_FULL_TEMPLATE,
  OUTDOOR_ENTERTAINING_TEMPLATE,
  OUTDOOR_STRUCTURES_TEMPLATE,
  OUTDOOR_LANDSCAPING_TEMPLATE,
  // Garage
  GARAGE_STANDARD_TEMPLATE,
  GARAGE_CONVERSION_TEMPLATE,
  GARAGE_ADU_TEMPLATE,
  GARAGE_FULL_TEMPLATE,
  // Basement
  BASEMENT_STANDARD_TEMPLATE,
  BASEMENT_ENTERTAINMENT_TEMPLATE,
];

/**
 * Get templates available for a specific room type
 */
export function getTemplatesForRoomType(roomType: RoomType): ProjectTypeTemplate[] {
  return PROJECT_TYPE_TEMPLATES.filter((t) => t.forRoomTypes.includes(roomType));
}

/**
 * Get a template by its ID
 */
export function getTemplateById(templateId: string): ProjectTypeTemplate | undefined {
  return PROJECT_TYPE_TEMPLATES.find((t) => t.id === templateId);
}
